/** Live cloud tip notifications via Phoenix Channels. */

import { Socket, type Channel } from "phoenix";
import { getCloudSession } from "./cloudAuth";
import { cloudApiBaseUrl } from "./cloudApiBase";
import { isCloudActivityBusy, onCloudActivityIdle } from "./cloudActivity";
import {
  resolveCloudProjectId,
  syncFromCloud,
  type CloudSnapshotHint,
  type SyncFromCloudResult,
} from "./cloudSync";
import { shouldSuppressRealtimeEcho } from "./cloudSnapshot";
import { LOCAL_PREF_KEYS } from "./localPrefs";
import { isTauri } from "./runtime";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

export type WatchProjectTipsOptions = {
  rootPath: string;
  onSynced?: (result: SyncFromCloudResult) => void | Promise<void>;
};

export type StopWatchProjectTips = () => void;

type ChannelTipPayload = {
  id?: string | null;
  git_head_oid?: string | null;
  gitHeadOid?: string | null;
  git_base_oid?: string | null;
  gitBaseOid?: string | null;
  updated_ref?: string | null;
  updatedRef?: string | null;
};

function cloudSocketUrl(): string {
  const parsed = new URL(cloudApiBaseUrl());
  parsed.protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
  parsed.pathname = "/socket";
  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

function hintFromPayload(
  cloudProjectId: string,
  payload?: ChannelTipPayload | null,
): CloudSnapshotHint {
  return {
    cloudProjectId,
    snapshotId: trimmedOrNull(payload?.id),
    gitHeadOid: trimmedOrNull(payload?.git_head_oid ?? payload?.gitHeadOid),
    gitBaseOid: trimmedOrNull(payload?.git_base_oid ?? payload?.gitBaseOid),
  };
}

function updatedRefOf(payload?: ChannelTipPayload | null): string | null {
  return trimmedOrNull(payload?.updated_ref ?? payload?.updatedRef);
}

async function pullIfNeeded(
  rootPath: string,
  hint: CloudSnapshotHint,
  updatedRef: string | null,
  onSynced?: WatchProjectTipsOptions["onSynced"],
): Promise<void> {
  if (
    shouldSuppressRealtimeEcho(
      rootPath,
      trimmedOrNull(hint.gitHeadOid),
      updatedRef,
    )
  ) {
    return;
  }
  if (isCloudActivityBusy(rootPath)) return;

  try {
    const result = await syncFromCloud(rootPath, "realtime", hint);
    await onSynced?.(result);
  } catch (error) {
    console.warn("[cloudRealtime] sync failed", error);
  }
}

export async function watchProjectTips(
  opts: WatchProjectTipsOptions,
): Promise<StopWatchProjectTips> {
  const noop: StopWatchProjectTips = () => {};
  if (isTauri() === false) return noop;

  const cloudProjectId = await resolveCloudProjectId(opts.rootPath);
  if (cloudProjectId === null) {
    console.warn("[cloudRealtime] no cloud project id; skip channel watch");
    return noop;
  }

  let socket: Socket | null = null;
  let channel: Channel | null = null;
  let paused = false;
  let stopped = false;
  let pending: { hint: CloudSnapshotHint; updatedRef: string | null } | null =
    null;

  const accessToken = (): string =>
    trimmedOrEmpty(getCloudSession()?.accessToken);

  const requestPull = (payload?: ChannelTipPayload | null): void => {
    if (stopped) return;
    const hint = hintFromPayload(cloudProjectId, payload);
    const updatedRef = updatedRefOf(payload);
    if (
      shouldSuppressRealtimeEcho(
        opts.rootPath,
        trimmedOrNull(hint.gitHeadOid),
        updatedRef,
      )
    ) {
      pending = null;
      return;
    }
    if (isCloudActivityBusy(opts.rootPath)) {
      pending = { hint, updatedRef };
      return;
    }
    pending = null;
    void pullIfNeeded(opts.rootPath, hint, updatedRef, opts.onSynced);
  };

  const leaveChannel = (): void => {
    if (channel !== null) {
      channel.leave();
      channel = null;
    }
  };

  const joinChannel = (): void => {
    if (socket === null || stopped || paused) return;
    leaveChannel();

    channel = socket.channel(`project:${cloudProjectId}`, {});
    channel
      .join()
      .receive("ok", (reply: ChannelTipPayload) => {
        requestPull(reply);
      })
      .receive("error", (reason) => {
        console.warn("[cloudRealtime] join failed", reason);
        leaveChannel();
      })
      .receive("timeout", () => {
        console.warn("[cloudRealtime] join timed out");
      });

    channel.on("snapshot_created", (payload: ChannelTipPayload) => {
      requestPull(payload);
    });
  };

  const connectSocket = (): void => {
    if (stopped || paused) return;
    if (accessToken().length === 0) return;

    if (socket === null) {
      socket = new Socket(cloudSocketUrl(), {
        params: () => ({ token: accessToken() }),
      });
      socket.onOpen(() => {
        if (stopped || paused) return;
        console.info("[cloudRealtime] connected", cloudSocketUrl());
        joinChannel();
      });
      socket.onError(() => {
        if (stopped || paused) return;
        console.warn("[cloudRealtime] socket error", cloudSocketUrl());
      });
    }

    socket.connect();
    if (socket.isConnected()) joinChannel();
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      paused = true;
      leaveChannel();
      return;
    }

    if (paused) {
      paused = false;
      connectSocket();
    }
  };

  const onWindowFocus = (): void => {
    if (stopped || document.visibilityState === "hidden") return;
    if (paused) {
      paused = false;
      connectSocket();
    }
  };

  const onStorage = (event: StorageEvent): void => {
    if (event.key !== LOCAL_PREF_KEYS.cloudSession) return;
    if (stopped || paused) return;
    leaveChannel();
    connectSocket();
  };

  const unsubscribeIdle = onCloudActivityIdle((root) => {
    if (root !== opts.rootPath || pending === null) return;
    const { hint, updatedRef } = pending;
    pending = null;
    void pullIfNeeded(opts.rootPath, hint, updatedRef, opts.onSynced);
  });

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("focus", onWindowFocus);
  window.addEventListener("storage", onStorage);

  connectSocket();

  return () => {
    stopped = true;
    paused = false;
    pending = null;
    unsubscribeIdle();
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("focus", onWindowFocus);
    window.removeEventListener("storage", onStorage);
    leaveChannel();
    if (socket !== null) {
      socket.disconnect();
      socket = null;
    }
  };
}
